<template>
  <div class="modal-overlay">
    <div class="modal-background" @click="$emit('closeModal')" />
    <div class="modal">
      <div class="title-close">
        <h1 class="modal-title">{{ $adminT('performances.newTicket', 'Create a new Ticket Type') }}</h1>
        <span class="material-icons" @click="$emit('close')"> close </span>
      </div>
      <text-field v-model.trim="form.name" admin-context :label="$adminT('performances.ticketName', 'Ticket Type name*')" type="text" />
      <div class="input-container">
        <text-field v-model.number="form.price" admin-context :label="$adminT('performances.price', 'Price (EUR)*')" type="number" />
      </div>
      <select-field
        v-model.number="form.venueId"
        admin-context
        :label="$adminT('performances.odeum', 'Odeum*')"
        :disabled="!!preselectedVenueId"
        :hint="
          preselectedVenueId ? $adminT('performances.fixedOdeum', 'Set by the performance this ticket type is created for.') : undefined
        "
        style="width: 100%"
        :items="
          organizationVenues.map(item => {
            return {
              ...item,
              id: Number(item.id),
            }
          })
        "
        item-select="id"
        item-render="name"
        @change="getSectorNames"
      />
      <select-field-multiple
        v-if="numberOfVenueSectors > 1"
        v-model="form.venueSection"
        admin-context
        style="width: 100%"
        :items="sectorItems"
        :placeholder="$adminT('performances.selectSector', 'Select sector')"
        :label="$adminT('performances.sector', 'Sector')"
        item-render="name"
        item-select="value"
      />
      <v-checkbox
        v-model="form.ticketSeller"
        :disabled="form.prepaidCoupon || form.membership || form.salesChannel || form.isAccessible"
        :label="$adminT('performances.boxOffice', 'Do not display on the web store and ticket sellers – This ticket type is only available at the box office.')"
      />
      <v-checkbox
        v-model="form.isAccessible"
        :disabled="form.ticketSeller || form.membership || form.salesChannel || form.prepaidCoupon"
      >
        <template v-slot:label>
          <span class="accessible-ticket-label">
            {{ $adminT('performances.accessible', 'Accessible ticket (for persons with disabilities)') }}
            <img
              class="accessible-ticket-icon"
              :src="require('../../../assets/accessible-seat.svg')"
              alt=""
            >
          </span>
        </template>
      </v-checkbox>
      <v-checkbox
        v-model="form.salesChannel"
        :disabled="form.prepaidCoupon || form.membership || form.ticketSeller || form.isAccessible"
        :label="$adminT('performances.sellerTicket', 'Available for ticket sellers – This ticket type is visible to designated ticket sellers, defined for each performance.')"
      />
      <v-checkbox
        v-model="form.membership"
        :disabled="form.prepaidCoupon || form.ticketSeller || form.salesChannel || form.isAccessible"
        :label="$adminT('performances.membership', 'Membership only – This ticket type will be available only if connected to a specific membership plan')"
      />
      <v-checkbox
        v-model="form.prepaidCoupon"
        :disabled="form.ticketSeller || form.membership || form.salesChannel || form.isAccessible"
        :label="$adminT('performances.package', 'Ticket package can be applied.')"
      />
      <div class="buttons">
        <main-button
          class="create-button"
          color="primary"
          :text="$adminT('performances.createTicket', 'Create ticket')"
          :disabled="isSubmitButtonDisabled()"
          @click="$emit('createTicket', form)"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ticket-type-create-modal',
  props: {
    organizationVenues: {
      type: Array,
      default: () => [],
    },
    // When created from a performance, the odeum is fixed: a ticket type made for another odeum
    // would never show up in that performance's ticket list.
    preselectedVenueId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      form: { venueId: this.preselectedVenueId ? Number(this.preselectedVenueId) : null },
      numberOfVenueSectors: 0,
    }
  },
  computed: {
    sectorItems() {
      const venue = this.organizationVenues.find(item => item.id == this.form.venueId) || {}
      const sectors = Object.keys(venue.predefinedSections || {})

      return sectors.length
        ? [{ value: 'All', name: this.$adminT('performances.allSectors', 'All') }, ...sectors.map(value => ({ value, name: value }))]
        : []
    },
  },
  created() {
    if (this.preselectedVenueId) {
      this.getSectorNames()
    }
  },
  methods: {
    getSectorNames() {
      this.form.venueSection = []
      const selectedVenue =
        this.organizationVenues.find(venue => venue.id == this.form.venueId) || {}

      const allVenueSections = { ...selectedVenue.predefinedSections }
      this.numberOfVenueSectors = Object.keys(allVenueSections).length

      return this.numberOfVenueSectors ? ['All', ...Object.keys(allVenueSections)] : []
    },
    isSubmitButtonDisabled() {
      const isPriceDefined = !!this.form.price || this.form.price === 0

      return !this.form.name || !isPriceDefined || !this.form.venueId
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  overflow-y: auto;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.modal-background {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #00000050;
}

.modal {
  text-align: left;
  background-color: white;
  width: 100%;
  position: relative;
  max-width: 40.75rem;
  padding: 2rem;
  border-radius: 20px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.modal::-webkit-scrollbar {
  display: none;
}

.modal-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

@media screen and (max-width: 700px) {
  .modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

.title-close {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.input-container {
  display: flex;
  align-items: center;
  width: 100%;
  column-gap: 1.5rem;
  justify-content: space-between;
}

.material-icons {
  cursor: pointer;
}

.accessible-ticket-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.accessible-ticket-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
</style>
